import React, { useState, useEffect } from 'react';
import { Unit, UnitFormData } from '../types/unit';
import FormSlider from './FormSlider';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface UnitFormSliderProps {
  workspaceId: string;
  unitId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Project {
  _id: string;
  name: string;
  builderId: string;
}

interface Model {
  _id: string;
  name: string;
  modelCollection?: string;
  bed?: number;
  bath?: number;
  den?: number;
  flex?: number;
  media?: number;
  area?: number;
  ceiling?: number;
  outdoorType?: string;
  outdoorArea?: number;
  views?: string[];
  buildingType?: string;
  eligibleToBuy?: {
    parking: boolean;
    locker: boolean;
    bicycle: boolean;
  };
}

const UnitFormSlider: React.FC<UnitFormSliderProps> = ({
  workspaceId,
  unitId,
  onSuccess,
  onCancel
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingModels, setLoadingModels] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [initialProjectId, setInitialProjectId] = useState('');
  const [initialModelId, setInitialModelId] = useState('');
  const [autoPopulatedFields, setAutoPopulatedFields] = useState<Partial<UnitFormData>>({});
  const [modelViews, setModelViews] = useState<string[]>([]);

  const initialFormData: UnitFormData = {
    projectId: initialProjectId,
    modelId: initialModelId,
    modelCollectionId: '',
    unitNo: '',
    unitEnrolmentNo: '',
    status: 'AVAILABLE',
    bed: autoPopulatedFields.bed || '',
    bath: autoPopulatedFields.bath || '',
    den: autoPopulatedFields.den || '',
    flex: autoPopulatedFields.flex || '',
    media: autoPopulatedFields.media || '',
    ceiling: autoPopulatedFields.ceiling || '',
    area: autoPopulatedFields.area || '',
    outdoorType: autoPopulatedFields.outdoorType || '',
    outdoorArea: autoPopulatedFields.outdoorArea || '',
    floorMarketing: '',
    floorLegal: '',
    unitNoMarketing: '',
    unitNoLegal: '',
    buildingType: autoPopulatedFields.buildingType || '',
    views: [],
    spaces: [],
    eligibleToBuy: autoPopulatedFields.eligibleToBuy || {
      parking: false,
      locker: false,
      bicycle: false
    },
    floorPosition: '1',
    unitPosition: '1',
    unitWidth: '110',
    unitHeight: '150'
  };

  const fetchModels = async (projectId: string, autoSelect: boolean = true) => {
    try {
      setLoadingModels(true);
      const response = await api.get(`/workspaces/${workspaceId}/models`, {
        params: {
          projectId,
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const modelsList = response.data.models;
      setModels(modelsList);

      // Auto-select first model only if autoSelect is true
      if (autoSelect && !unitId && modelsList.length > 0) {
        const firstModel = modelsList[0];
        setInitialModelId(firstModel._id);
        setSelectedModelId(firstModel._id);
        // Set model views
        setModelViews(firstModel.views || []);
        // Auto-populate model fields
        setAutoPopulatedFields({
          bed: firstModel.bed?.toString() || '',
          bath: firstModel.bath?.toString() || '',
          den: firstModel.den?.toString() || '',
          flex: firstModel.flex?.toString() || '',
          media: firstModel.media?.toString() || '',
          area: firstModel.area?.toString() || '',
          ceiling: firstModel.ceiling?.toString() || '',
          outdoorType: firstModel.outdoorType || '',
          outdoorArea: firstModel.outdoorArea?.toString() || '',
          buildingType: firstModel.buildingType || '',
          eligibleToBuy: firstModel.eligibleToBuy || {
            parking: false,
            locker: false,
            bicycle: false
          }
        });
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchProjects = async (autoSelect: boolean = true) => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsList = response.data.projects;
      setProjects(projectsList);

      // Auto-select first project only if autoSelect is true
      if (autoSelect && !unitId && projectsList.length > 0) {
        const firstProjectId = projectsList[0]._id;
        setInitialProjectId(firstProjectId);
        setSelectedProjectId(firstProjectId);
        fetchModels(firstProjectId, true); // true = auto-select first model
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    if (!unitId) {
      fetchProjects();
    }
  }, [workspaceId, unitId]);

  const validateForm = (data: UnitFormData): string | null => {
    if (!data.projectId) {
      return 'Project selection is required';
    }
    if (!data.modelId) {
      return 'Model selection is required';
    }
    if (!data.unitNo.trim()) {
      return 'Unit number is required';
    }
    if (!data.floorMarketing) {
      return 'Floor marketing is required';
    }
    return null;
  };

  const transformFetchData = (response: any): UnitFormData => {
    const unit: Unit = response.unit;
    return {
      projectId: unit.projectId || '',
      modelId: unit.modelId || '',
      modelCollectionId: unit.modelCollection || '',
      unitNo: unit.unitNo || '',
      unitEnrolmentNo: '',
      status: unit.status || 'AVAILABLE',
      bed: unit.bed?.toString() || '',
      bath: unit.bath?.toString() || '',
      den: unit.den?.toString() || '',
      flex: unit.flex?.toString() || '',
      media: unit.media?.toString() || '',
      ceiling: unit.ceiling?.toString() || '',
      area: unit.area?.toString() || '',
      outdoorType: unit.outdoorType || '',
      outdoorArea: unit.outdoorArea?.toString() || '',
      floorMarketing: unit.floorMarketing?.toString() || '',
      floorLegal: unit.floorLegal || '',
      unitNoMarketing: unit.unitNoMarketing?.toString() || '',
      unitNoLegal: unit.unitNoLegal || '',
      buildingType: unit.buildingType || '',
      views: unit.views || [],
      spaces: unit.spaces || [],
      eligibleToBuy: unit.eligibleToBuy || { parking: false, locker: false, bicycle: false },
      floorPosition: unit.buildingView?.floor?.toString() || '1',
      unitPosition: unit.buildingView?.unit?.toString() || '1',
      unitWidth: unit.buildingView?.width?.toString() || '110',
      unitHeight: unit.buildingView?.height?.toString() || '150'
    };
  };

  const transformSubmitData = (data: UnitFormData) => {
    // Get builderId from selected project
    const selectedProject = projects.find(p => p._id === data.projectId);
    return {
      builderId: selectedProject?.builderId,
      projectId: data.projectId,
      modelId: data.modelId,
      collection: data.modelCollectionId || undefined,
      unitNo: data.unitNo.trim(),
      unitNoLegal: data.unitNoLegal?.trim() || undefined,
      unitNoMarketing: data.unitNoMarketing ? parseInt(data.unitNoMarketing) : undefined,
      floorLegal: data.floorLegal?.trim() || undefined,
      floorMarketing: data.floorMarketing ? parseInt(data.floorMarketing) : undefined,
      status: data.status,
      bed: data.bed ? parseInt(data.bed) : undefined,
      bath: data.bath ? parseInt(data.bath) : undefined,
      den: data.den ? parseInt(data.den) : undefined,
      flex: data.flex ? parseInt(data.flex) : undefined,
      media: data.media ? parseInt(data.media) : undefined,
      ceiling: data.ceiling ? parseFloat(data.ceiling) : undefined,
      area: data.area ? parseFloat(data.area) : undefined,
      outdoorType: data.outdoorType || undefined,
      outdoorArea: data.outdoorArea ? parseFloat(data.outdoorArea) : undefined,
      buildingType: data.buildingType || undefined,
      views: data.views,
      eligibleToBuy: data.eligibleToBuy,
      buildingView: {
        floor: data.floorPosition ? parseInt(data.floorPosition) : 1,
        unit: data.unitPosition ? parseInt(data.unitPosition) : 1,
        width: data.unitWidth ? parseInt(data.unitWidth) : 110,
        height: data.unitHeight ? parseInt(data.unitHeight) : 150
      }
    };
  };

  // Use effect to update form data after async loading completes
  useEffect(() => {
    // This will trigger a re-render when the initial data is loaded
  }, [initialProjectId, initialModelId]);

  // Load model views when models are loaded and we have a selected model (for edit mode)
  useEffect(() => {
    if (models.length > 0 && selectedModelId) {
      const selectedModel = models.find(m => m._id === selectedModelId);
      if (selectedModel && modelViews.length === 0) {
        setModelViews(selectedModel.views || []);
      }
    }
  }, [models, selectedModelId, modelViews.length]);

  if (!unitId && loadingProjects) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <FormSlider
      key={`${initialProjectId}-${initialModelId}`}
      workspaceId={workspaceId}
      itemId={unitId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/units`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformFetchData={transformFetchData}
      transformSubmitData={transformSubmitData}
      formId="unit-form"
    >
      {({ formData, handleInputChange, handleFieldChange, loading }) => (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            {/* Project */}
            <div className="form-group">
              <label htmlFor="projectId" className="form-label">
                Project
              </label>
              <select
                id="projectId"
                name="projectId"
                className="form-input"
                value={formData.projectId}
                onChange={(e) => {
                  handleInputChange(e);
                  setSelectedProjectId(e.target.value);
                  setSelectedModelId('');
                  setModels([]);
                  setModelViews([]);
                  handleFieldChange('views', []);
                  if (e.target.value) {
                    fetchModels(e.target.value);
                  }
                }}
                required
                disabled={loading}
              >
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Model */}
            <div className="form-group">
              <label htmlFor="modelId" className="form-label">
                Model
              </label>
              {loadingModels ? (
                <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : (
                <select
                  id="modelId"
                  name="modelId"
                  className="form-input"
                  value={formData.modelId}
                  onChange={(e) => {
                    handleInputChange(e);
                    setSelectedModelId(e.target.value);
                    // Auto-populate model fields
                    const selectedModel = models.find(m => m._id === e.target.value);
                    if (selectedModel) {
                      handleFieldChange('bed', selectedModel.bed?.toString() || '');
                      handleFieldChange('bath', selectedModel.bath?.toString() || '');
                      handleFieldChange('den', selectedModel.den?.toString() || '');
                      handleFieldChange('flex', selectedModel.flex?.toString() || '');
                      handleFieldChange('media', selectedModel.media?.toString() || '');
                      handleFieldChange('area', selectedModel.area?.toString() || '');
                      handleFieldChange('ceiling', selectedModel.ceiling?.toString() || '');
                      handleFieldChange('outdoorType', selectedModel.outdoorType || '');
                      handleFieldChange('outdoorArea', selectedModel.outdoorArea?.toString() || '');
                      handleFieldChange('modelCollectionId', selectedModel.modelCollection || '');
                      // Set model views and clear selected views
                      setModelViews(selectedModel.views || []);
                      handleFieldChange('views', []);
                    }
                  }}
                  required
                  disabled={loading || !formData.projectId}
                >
                  {models.map((model) => (
                    <option key={model._id} value={model._id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
            {/* Unit Number */}
            <div className="form-group">
              <label htmlFor="unitNo" className="form-label">
                Unit Number
              </label>
              <input
                type="text"
                id="unitNo"
                name="unitNo"
                className="form-input"
                value={formData.unitNo}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>

            {/* Unit Enrolment Number */}
            <div className="form-group">
              <label htmlFor="unitEnrolmentNo" className="form-label">
                Unit Enrolment No:
              </label>
              <input
                type="text"
                id="unitEnrolmentNo"
                name="unitEnrolmentNo"
                className="form-input"
                value={formData.unitEnrolmentNo}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            {/* Status */}
            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status:
              </label>
              <select
                id="status"
                name="status"
                className="form-input"
                value={formData.status}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="AVAILABLE">AVAILABLE</option>
                <option value="RESERVED">RESERVED</option>
                <option value="SOLD">SOLD</option>
                <option value="UNAVAILABLE">UNAVAILABLE</option>
              </select>
            </div>

            {/* Building Type */}
            <div className="form-group">
              <label htmlFor="buildingType" className="form-label">
                Building Type
              </label>
              <select
                id="buildingType"
                name="buildingType"
                className="form-input"
                value={formData.buildingType}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value=""></option>
                <option value="Condominium">Condominium</option>
                <option value="Townhouse">Townhouse</option>
                <option value="Detached">Detached</option>
                <option value="Semi-Detached">Semi-Detached</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px' }}>
            {/* Bed */}
            <div className="form-group">
              <label htmlFor="bed" className="form-label">
                Bed:
              </label>
              <input
                type="number"
                id="bed"
                name="bed"
                className="form-input"
                value={formData.bed}
                onChange={handleInputChange}
                disabled={loading}
                min="0"
              />
            </div>

            {/* Bath */}
            <div className="form-group">
              <label htmlFor="bath" className="form-label">
                Bath:
              </label>
              <input
                type="number"
                id="bath"
                name="bath"
                className="form-input"
                value={formData.bath}
                onChange={handleInputChange}
                disabled={loading}
                min="0"
              />
            </div>

            {/* Den */}
            <div className="form-group">
              <label htmlFor="den" className="form-label">
                Den:
              </label>
              <input
                type="number"
                id="den"
                name="den"
                className="form-input"
                value={formData.den}
                onChange={handleInputChange}
                disabled={loading}
                min="0"
              />
            </div>

            {/* Flex */}
            <div className="form-group">
              <label htmlFor="flex" className="form-label">
                Flex:
              </label>
              <input
                type="number"
                id="flex"
                name="flex"
                className="form-input"
                value={formData.flex}
                onChange={handleInputChange}
                disabled={loading}
                min="0"
              />
            </div>

            {/* Media */}
            <div className="form-group">
              <label htmlFor="media" className="form-label">
                Media:
              </label>
              <input
                type="number"
                id="media"
                name="media"
                className="form-input"
                value={formData.media}
                onChange={handleInputChange}
                disabled={loading}
                min="0"
              />
            </div>

            {/* Ceiling */}
            <div className="form-group">
              <label htmlFor="ceiling" className="form-label">
                Ceiling (Ft.):
              </label>
              <input
                type="number"
                id="ceiling"
                name="ceiling"
                className="form-input"
                value={formData.ceiling}
                onChange={handleInputChange}
                disabled={loading}
                step="0.1"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
            {/* Area */}
            <div className="form-group">
              <label htmlFor="area" className="form-label">
                Area (Sq.Ft):
              </label>
              <input
                type="number"
                id="area"
                name="area"
                className="form-input"
                value={formData.area}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            {/* Outdoor Type */}
            <div className="form-group">
              <label htmlFor="outdoorType" className="form-label">
                Outdoor Type:
              </label>
              <select
                id="outdoorType"
                name="outdoorType"
                className="form-input"
                value={formData.outdoorType}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value=""></option>
                <option value="Balcony">Balcony</option>
                <option value="Terrace">Terrace</option>
                <option value="Patio">Patio</option>
                <option value="None">None</option>
              </select>
            </div>

            {/* Outdoor Area */}
            <div className="form-group">
              <label htmlFor="outdoorArea" className="form-label">
                Outdoor Area (Sq.Ft):
              </label>
              <input
                type="number"
                id="outdoorArea"
                name="outdoorArea"
                className="form-input"
                value={formData.outdoorArea}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
            {/* Floor Legal */}
            <div className="form-group">
              <label htmlFor="floorLegal" className="form-label">
                Floor Legal:
              </label>
              <input
                type="text"
                id="floorLegal"
                name="floorLegal"
                className="form-input"
                value={formData.floorLegal}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            {/* Floor Marketing */}
            <div className="form-group">
              <label htmlFor="floorMarketing" className="form-label">
                Floor Marketing
              </label>
              <input
                type="number"
                id="floorMarketing"
                name="floorMarketing"
                className="form-input"
                value={formData.floorMarketing}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>

            {/* Unit Number Marketing */}
            <div className="form-group">
              <label htmlFor="unitNoMarketing" className="form-label">
                Unit No Marketing
              </label>
              <input
                type="number"
                id="unitNoMarketing"
                name="unitNoMarketing"
                className="form-input"
                value={formData.unitNoMarketing}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            {/* Unit Number Legal */}
            <div className="form-group">
              <label htmlFor="unitNoLegal" className="form-label">
                Unit No Legal:
              </label>
              <input
                type="text"
                id="unitNoLegal"
                name="unitNoLegal"
                className="form-input"
                value={formData.unitNoLegal}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>
          </div>

          {/* Views */}
          <div className="form-group">
            <label className="form-label">Views:</label>
            {modelViews.length === 0 ? (
              <p style={{ color: '#9CA3AF', fontStyle: 'italic', margin: '8px 0' }}>
                {formData.modelId ? 'No views configured for this model.' : 'Select a model to see available views.'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '8px' }}>
                {modelViews.map((view) => (
                  <label key={view} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.views.includes(view)}
                      onChange={(e) => {
                        const newViews = e.target.checked
                          ? [...formData.views, view]
                          : formData.views.filter(v => v !== view);
                        handleFieldChange('views', newViews);
                      }}
                      disabled={loading}
                    />
                    {view}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Eligible to Buy */}
          <div className="form-group">
            <label className="form-label">Eligible to Buy:</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.eligibleToBuy.parking}
                  onChange={(e) => handleFieldChange('eligibleToBuy', {
                    ...formData.eligibleToBuy,
                    parking: e.target.checked
                  })}
                  disabled={loading}
                />
                Parking
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.eligibleToBuy.locker}
                  onChange={(e) => handleFieldChange('eligibleToBuy', {
                    ...formData.eligibleToBuy,
                    locker: e.target.checked
                  })}
                  disabled={loading}
                />
                Locker
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.eligibleToBuy.bicycle}
                  onChange={(e) => handleFieldChange('eligibleToBuy', {
                    ...formData.eligibleToBuy,
                    bicycle: e.target.checked
                  })}
                  disabled={loading}
                />
                Bicycle
              </label>
            </div>
          </div>

          {/* Building View Section */}
          <h3>Building View</h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
            {/* Floor Position */}
            <div className="form-group">
              <label htmlFor="floorPosition" className="form-label">
                Floor Position
              </label>
              <input
                type="number"
                id="floorPosition"
                name="floorPosition"
                className="form-input"
                value={formData.floorPosition}
                onChange={handleInputChange}
                required
                disabled={loading}
                min="1"
              />
            </div>

            {/* Unit Position */}
            <div className="form-group">
              <label htmlFor="unitPosition" className="form-label">
                Unit Position
              </label>
              <input
                type="number"
                id="unitPosition"
                name="unitPosition"
                className="form-input"
                value={formData.unitPosition}
                onChange={handleInputChange}
                required
                disabled={loading}
                min="1"
              />
            </div>

            {/* Unit Width */}
            <div className="form-group">
              <label htmlFor="unitWidth" className="form-label">
                Unit Width (px)
              </label>
              <input
                type="number"
                id="unitWidth"
                name="unitWidth"
                className="form-input"
                value={formData.unitWidth}
                onChange={handleInputChange}
                required
                disabled={loading}
                min="1"
              />
            </div>

            {/* Unit Height */}
            <div className="form-group">
              <label htmlFor="unitHeight" className="form-label">
                Unit Height (px)
              </label>
              <input
                type="number"
                id="unitHeight"
                name="unitHeight"
                className="form-input"
                value={formData.unitHeight}
                onChange={handleInputChange}
                required
                disabled={loading}
                min="1"
              />
            </div>
          </div>
        </>
      )}
    </FormSlider>
  );
};

export default UnitFormSlider;
